import {
    Box, Button, Card, CircularProgress, FormControl, FormControlLabel,
    Link, MenuItem, Select, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import { CSVLink } from 'react-csv';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterLuxon} from '@mui/x-date-pickers/AdapterLuxon';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
    DataGrid,
    GridColDef,
    GridCsvGetRowsToExportParams,
    GridRenderCellParams,
    GridRowsProp,
    GridSortModel,
    GridToolbar,
    GridValueGetterParams,
    gridSortedRowIdsSelector,
} from '@mui/x-data-grid';
import {useDispatch, useSelector} from 'react-redux';
import {Selector} from '../../state/selector';
import Command from '../../state/actions/command';
import {DateTime} from 'luxon';
import {testCategories} from '../../util/testCategoryType';
import {calculateBGScore, getHighestVotes} from '../../util/bgScore';
import {Company} from '../../state/types/Company';

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
};

const getUnfilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams) =>
    gridSortedRowIdsSelector(apiRef);

const AdminPage = () => {
    const dispatch = useDispatch();
    const companyList = useSelector(Selector.Company.companyList);
    const userList = useSelector(Selector.User.users);
    const companyStatusMap = useSelector(Selector.Company.companyStatusMap);
    const companyVotesByDateMap = useSelector(Selector.Company.companyVotesByDateMap);
    const isAdmin = useSelector(Selector.User.isAdmin);
    const isEditor = useSelector(Selector.User.isEditor);
    const activeVersion = useSelector(Selector.QuestionList.activeVersion);

    const [selectedVersion, setSelectedVersion] = useState<number | 'all' | undefined>(undefined);
    const [showArchived, setShowArchived] = useState<boolean>(false);
    const resolvedVersion = selectedVersion ?? activeVersion ?? 5;
    const effectiveVersion = resolvedVersion === 'all' ? activeVersion : resolvedVersion;

    const isAllVersions = resolvedVersion === 'all';
    const ALL_VERSIONS_INDEX = 9999;
    const dataVersion = isAllVersions ? ALL_VERSIONS_INDEX : effectiveVersion;

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {field: 'bgs', sort: 'desc' },
        {field: 'totalCount', sort: 'desc' },
        {field: '_5', sort: 'desc' },
    ]);

    // Date filter state
    const [startDate, setStartDate] = useState<DateTime | null>(
        DateTime.fromObject({ year: 2026, month: 1, day: 1 }),
    );
    const [endDate, setEndDate] = useState<DateTime | null>(DateTime.now());
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasFilteredData, setHasFilteredData] = useState<boolean>(false);
    const filterRequestCount = useRef(0);
    const lastProcessedCount = useRef(0);

    const loadUserData = async () => {
        const userIds: any[] = [];
        const loadedUsers: any[] = [];
        Object.keys(userList).forEach(key => {
            loadedUsers.push(key);
        });
        companyList.forEach(company => {
            if (!company.hidden && company.tester &&
                !userIds.includes(company.tester) && !loadedUsers.includes(company.tester))
            {
                userIds.push(company.tester);
                dispatch(Command.User.fetchUser({userId: company.tester}));
            }
        });
    };
    useEffect(() => {
        if (companyList.length > 0) {
            loadUserData();
        }
    }, [companyList]);

    // Handle filter submission
    const handleFilter = () => {
        if (!startDate || !endDate) {
            return;
        }

        const startDateISO = startDate.toISODate();
        const endDateISO = endDate.toISODate();

        if (!startDateISO || !endDateISO) {
            return;
        }

        filterRequestCount.current += 1;
        setIsLoading(true);
        setHasFilteredData(false);

        dispatch(Command.Company.fetchCompanyVotesByDate({
            startDate: startDateISO,
            endDate: endDateISO,
            ...(resolvedVersion !== 'all' ? { version: resolvedVersion } : {}),
        }));
    };

    // Handle reset - clear filtered data and return to original companyList
    const handleReset = () => {
        setHasFilteredData(false);
    };

    // Watch for filtered data - fires when API updates the map
    useEffect(() => {
        if (filterRequestCount.current > lastProcessedCount.current) {
            lastProcessedCount.current = filterRequestCount.current;
            // Only set hasFilteredData to true if we actually received data
            const hasData = Object.keys(companyVotesByDateMap).length > 0;
            setHasFilteredData(hasData);
            setIsLoading(false);
        }
    }, [companyVotesByDateMap]);

    // Disable filter button logic
    const isFilterDisabled = !startDate || !endDate || (startDate > endDate) || isLoading;

    const aggregateAllVersionVoting = (voting: any) => {
        const agg = { _1: 0, _2: 0, _3: 0, _4: 0, _5: 0, votes: 0, sum: 0 };
        if (!voting) return agg;
        Object.values(voting).forEach((v: any) => {
            if (!v) return;
            agg._1 += v._1 || 0;
            agg._2 += v._2 || 0;
            agg._3 += v._3 || 0;
            agg._4 += v._4 || 0;
            agg._5 += v._5 || 0;
            agg.votes += v.votes || 0;
            agg.sum += v.sum || 0;
        });
        return agg;
    };

    const rows: GridRowsProp = companyList
        .filter(company => showArchived || !company.hidden)
        .filter(company => isAllVersions
            ? company.bookings
                ? Object.values(company.bookings).some(b => b.marketing)
                : false
            : company.bookings?.[resolvedVersion as number]?.marketing ?? false,
        )
        .map(company => {
            const extra: Record<string, any> = {
                companyStatus: companyStatusMap[company.id],
            };

            if (hasFilteredData || isAllVersions) {
                const votingData = { ...(company.voting || {}) };
                const filtered = companyVotesByDateMap[company.id]?.statistics
                    ?? { _1: 0, _2: 0, _3: 0, _4: 0, _5: 0, sum: 0, votes: 0 };

                if (hasFilteredData) {
                    if (isAllVersions) {
                        votingData[ALL_VERSIONS_INDEX] = filtered;
                    } else {
                        votingData[effectiveVersion] = filtered;
                    }
                } else {
                    votingData[ALL_VERSIONS_INDEX] = aggregateAllVersionVoting(votingData);
                }

                extra.voting = votingData;
            }

            // Google ratings: use selected version if available, otherwise fall back to latest.
            // For "all versions", show the latest as it's most accurate.
            const grObj: Record<string, any> = company.googleRating || {};
            const grKeys = Object.keys(grObj).filter(k => grObj[k]?.rating);
            if (grKeys.length > 0) {
                const latestKey = grKeys.reduce((a, b) => (Number(a) > Number(b) ? a : b));
                const googleRatingData: Record<string, any> = { ...grObj };
                if (isAllVersions) {
                    googleRatingData[ALL_VERSIONS_INDEX] = grObj[latestKey];
                } else if (!grObj[dataVersion]?.rating) {
                    googleRatingData[dataVersion] = grObj[latestKey];
                }
                extra.googleRating = googleRatingData;
            }

            return { ...company, ...extra };
        });

    /* eslint-disable max-len */
    const columns: GridColDef[] = [
        {
            field: 'restaurantName',
            headerName: 'Unternehmen',
            minWidth: 200,
            flex: 5,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link
                        href={`/companies/${params.row.id}/base-data`}
                        underline={'hover'}
                    >
                        {params.value}
                    </Link>
                );
            },
        },
        { field: 'zip', headerName: 'Plz', minWidth: 133, flex: 2, hide: true},
        { field: 'city', headerName: 'Ort', minWidth: 133, flex: 2, hide: true },
        { field: 'street', headerName: 'Strasse', minWidth: 133, flex: 2, hide: true },
        { field: 'phone', headerName: 'Telefon', minWidth: 133, flex: 2, hide: true },
        { field: 'email', headerName: 'E-Mail', minWidth: 200, flex: 3, hide: true },
        { field: 'contactPerson', headerName: 'Ansprechpartner', minWidth: 200, flex: 3, hide: true },
        {
            field: 'bgs',
            headerName: 'BGS',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                if (!params.row.voting[dataVersion]?.sum || !params.row.voting[dataVersion]?.votes) {
                    return 0;
                }
                return calculateBGScore(params.row as Company, highestVotes, dataVersion);
            },
            renderCell: (params: GridRenderCellParams) => {
                const score = parseFloat(params.value) || 0;
                const backgroundColor = score === 0 ? 'rgb(222,222,222)' :
                    `rgba(${255 - (score - 1) * 64}, ${64 + (score - 1) * 48}, 64, 0.8)`;
                return (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor,
                            color: '#fff',
                            textShadow: '0 0 2px black',
                            borderRadius: '4px',
                        }}
                    >
                        {params.value}
                    </Box>
                );
            },
            renderHeader: (params) => (
                <Tooltip title={'Brandenburger Gastlichkeits Score'}>
                    <span>{params.colDef.headerName}</span>
                </Tooltip>
            ),
        },
        {
            field: 'totalCount',
            headerName: 'Σ',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.voting[dataVersion]?.votes ?? 0;
            },
        },
        {
            field: 'averageScore',
            headerName: '%',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                if (!params.row.voting[dataVersion]?.sum || !params.row.voting[dataVersion]?.votes) {
                    return 0;
                }
                return (params.row.voting[dataVersion]?.sum /
                    params.row.voting[dataVersion]?.votes).toFixed(1);
            },
        },
        {
            field: '1stars',
            headerName: '1★',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.voting[dataVersion]?._1 ?? 0;
            },
        },
        {
            field: '2stars',
            headerName: '2★',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.voting[dataVersion]?._2 ?? 0;
            },
        },
        {
            field: '3stars',
            headerName: '3★',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.voting[dataVersion]?._3 ?? 0;
            },
        },
        {
            field: '4stars',
            headerName: '4★',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.voting[dataVersion]?._4 ?? 0;
            },
        },
        {
            field: '5stars',
            headerName: '5★',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.voting[dataVersion]?._5 ?? 0;
            },
        },
        {
            field: 'googleRating',
            headerName: 'Google',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                if (
                    !params.row.googleRating[dataVersion]?.rating ||
                    !params.row.googleRating[dataVersion]?.user_ratings_total
                ) {
                    return '-';
                }
                return params.row.googleRating[dataVersion]?.rating +
                    ' (' + params.row.googleRating[dataVersion]?.user_ratings_total + ')';
            },
        },
        {
            field: 'totalAverage',
            headerName: '+ BGS',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                if (
                    !params.row.googleRating[dataVersion]?.rating ||
                    !params.row.googleRating[dataVersion]?.user_ratings_total
                ) {
                    return '-';
                }
                if (!params.row.voting[dataVersion]?.sum || !params.row.voting[dataVersion]?.votes) {
                    return '-';
                }
                return Number((
                    (calculateBGScore(params.row as Company, highestVotes, dataVersion) + params.row.googleRating[dataVersion]?.rating) / 2
                ).toFixed(1));
            },
        },
        {
            field: 'totalAverage1.5',
            headerName: '+ BGS (1.5)',
            minWidth: 25,
            valueGetter: (params: GridValueGetterParams) => {
                const google = params.row.googleRating[dataVersion];
                if (!params.row.voting[dataVersion]?.sum || !params.row.voting[dataVersion]?.votes) {
                    return '-';
                }
                const votes = params.row.voting[dataVersion]?.votes + (google?.user_ratings_total || 0);
                const sum = params.row.voting[dataVersion]?.sum +
                    ((google?.rating || 0) * (google?.user_ratings_total || 0));

                // Prevent division by zero
                if (votes === 0) {
                    return 0;
                }

                const score = sum / votes;

                // Prevent division by zero in power calculation
                if (highestVotesWithGoogle === 0) {
                    return 0;
                }

                return Number((
                    Math.pow(
                        score,
                        (((votes / highestVotesWithGoogle) ** 0.4) * 0.7) + 0.3,
                    )
                ).toFixed(1));
            },
        },
        { field: 'travelRegion', headerName: 'Reiseregion', minWidth: 133, flex: 3 },
        {
            field: 'testCategory',
            headerName: 'Testkategorie',
            minWidth: 133,
            flex: 3,
            renderCell: (params: GridRenderCellParams) => {
                return testCategories[params.row.testCategory ?? ''] ?? '-';
            },
        },
    ];

    // Calculate statistics based on filtered data or current voting data
    let valueRows: any[];
    let overallVotes: number;
    let overallScore: string;
    let highestScore: string | number;
    let lowestScore: string | number;
    let highestVotes: number;

    if (hasFilteredData) {
        // Use filtered data from companyVotesByDateMap
        const filteredRows = rows.filter(row => {
            const filteredData = companyVotesByDateMap[row.id];
            return filteredData && filteredData.statistics.votes > 0 && filteredData.statistics.sum > 0;
        });

        overallVotes = filteredRows.reduce((sum, row) => {
            const filteredData = companyVotesByDateMap[row.id];
            return sum + (filteredData?.statistics.votes || 0);
        }, 0);

        overallScore = (overallVotes > 0 ? filteredRows.reduce((sum, row) => {
            const filteredData = companyVotesByDateMap[row.id];
            return sum + (filteredData?.statistics.sum || 0);
        }, 0) / overallVotes : 0).toFixed(1);

        highestScore = filteredRows.reduce((max: number, row) => {
            const filteredData = companyVotesByDateMap[row.id];
            const score = Number(((filteredData?.statistics.sum || 0) /
                (filteredData?.statistics.votes || 1)).toFixed(1));
            return score > max ? score : max;
        }, 0);

        lowestScore = filteredRows.reduce((min: number, row) => {
            const filteredData = companyVotesByDateMap[row.id];
            const score = Number(((filteredData?.statistics.sum || 0) /
                (filteredData?.statistics.votes || 1)).toFixed(1));
            return score < min ? score : min;
        }, 5.0);

        highestVotes = filteredRows.reduce((max, row) => {
            const filteredData = companyVotesByDateMap[row.id];
            const votes = filteredData?.statistics.votes || 0;
            return votes > max ? votes : max;
        }, 0);

        valueRows = filteredRows;
    } else {
        // Use current voting data from dataVersion
        valueRows = rows.filter(row => {
            return row.voting[dataVersion]?.votes > 0 &&
                row.voting[dataVersion]?.sum > 0;
        });

        overallVotes = valueRows.reduce((sum, row) =>
            sum + (row.voting[dataVersion]?.votes || 0), 0);

        overallScore = (overallVotes > 0 ? valueRows.reduce((sum, row) =>
            sum + (row.voting[dataVersion]?.sum || 0), 0) / overallVotes : 0).toFixed(1);

        highestScore = valueRows.reduce((max, row) => {
            const score =
                ((row.voting[dataVersion]?.sum || 0) / (row.voting[dataVersion]?.votes || 1)).toFixed(1);
            return score > max ? score : max;
        }, 0);

        lowestScore = valueRows.reduce((min, row) => {
            const score =
                ((row.voting[dataVersion]?.sum || 0) / (row.voting[dataVersion]?.votes || 1)).toFixed(1);
            return score < min ? score : min;
        }, 5.0);

        highestVotes = getHighestVotes(valueRows, dataVersion);
    }
    const highestVotesWithGoogle = valueRows.reduce((max, row) => {
        const votes = (row.voting[dataVersion]?.votes || 0) + (row.googleRating[dataVersion]?.user_ratings_total || 0);
        return votes > max ? votes : max;
    }, 0);

    // Overview and BG-Score Export
    const headers = columns.map(column => ({
        label: column.headerName || column.field,
        key: column.field,
    }));
    const data = rows.map(row => {
        const rowData: Record<string, any> = {};
        columns.forEach(column => {
            rowData[column.field] = row[column.field];
        });
        const votes = row.voting[dataVersion]?.votes || 0;
        const sum = row.voting[dataVersion]?.sum || 0;

        rowData['averageScore'] = votes > 0 ? `'${(sum / votes).toFixed(1)}` : "'0";
        rowData['totalCount'] = votes;
        rowData['1stars'] = row.voting[dataVersion]?._1 || 0;
        rowData['2stars'] = row.voting[dataVersion]?._2 || 0;
        rowData['3stars'] = row.voting[dataVersion]?._3 || 0;
        rowData['4stars'] = row.voting[dataVersion]?._4 || 0;
        rowData['5stars'] = row.voting[dataVersion]?._5 || 0;
        rowData['googleRating'] =
            (!row.googleRating[dataVersion]?.rating || !row.googleRating[dataVersion]?.user_ratings_total) ? '-' :
                row.googleRating[dataVersion]?.rating.toString() + ' (' + row.googleRating[dataVersion]?.user_ratings_total.toString() + ')';

        if (votes === 0) {
            rowData['bgs'] = '0';
            rowData['totalAverage'] = '-';
            rowData['totalAverage1.5'] = '-';
            return rowData;
        }

        const bgs = calculateBGScore(row as Company, highestVotes, dataVersion);
        rowData['bgs'] = bgs === 0 ? '0' : "'" + bgs;
        rowData['totalAverage'] = "'" + ((bgs + row.googleRating[dataVersion]?.rating) / 2).toFixed(1);
        if (!row.googleRating[dataVersion]?.rating || !row.googleRating[dataVersion]?.user_ratings_total) {
            rowData['totalAverage1.5'] = '-';
            return rowData;
        }
        const google = row.googleRating[dataVersion];
        const votesWithGoogle = votes + (google?.user_ratings_total || 0);
        const sumWithGoogle = sum + ((google?.rating || 0) * (google?.user_ratings_total || 0));

        if (votesWithGoogle === 0 || highestVotesWithGoogle === 0) {
            rowData['totalAverage1.5'] = "'0";
        } else {
            const scoreWithGoogle = sumWithGoogle / votesWithGoogle;
            rowData['totalAverage1.5'] = "'" + Number((
                Math.pow(
                    scoreWithGoogle,
                    (((votesWithGoogle / highestVotesWithGoogle) ** 0.4) * 0.7) + 0.3,
                )
            ).toFixed(1));
        }
        return rowData;
    });
    const date = DateTime.now().toFormat('yyyy-MM-dd_HH-mm');
    const csvReport = {
        data: data,
        headers: headers,
        separator: ';',
        filename: hasFilteredData ?
            `favorite-restaurant_${startDate?.toFormat('yyyy-MM-dd')}_to_${endDate?.toFormat('yyyy-MM-dd')}.csv` :
            `favorite-restaurant_${date}.csv`,
    };

    if (!isAdmin && !isEditor) {
        return (
            <Box sx={{ padding: 2 }}>
                <Typography variant={'h4'} sx={{ textAlign: 'center' }}>
                    Diese Seite ist nur für Administratoren zugänglich.
                </Typography>
            </Box>
        );
    }

    const cardStyle = {
        minWidth: '200px',
        flex: 1,
        maxWidth: '350px',
        padding: '15px',
        borderRadius: '10px',
        gap: 3,
        boxShadow: '10px 10px 15px rgba(0, 0, 0, 0.3)',
    };

    return (
        <Box sx={styles.root}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant={'h1'}>
                    Am Lieblingslokal teilnehmende Unternehmen
                </Typography>
                <FormControl size={'small'} sx={{ minWidth: 150 }}>
                    <Select
                        value={resolvedVersion}
                        onChange={(e) => {
                            setSelectedVersion(e.target.value as number | 'all');
                            setHasFilteredData(false);
                        }}
                    >
                        <MenuItem value={'all'}>Alle Versionen</MenuItem>
                        {Array.from({ length: activeVersion ?? 5 }, (_, i) => i + 1).reverse().map(v => (
                            <MenuItem key={v} value={v}>Version {v}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            size={'small'}
                        />
                    }
                    label={'Archivierte anzeigen'}
                />
            </Box>

            {/* Date Range Filter */}
            <LocalizationProvider dateAdapter={AdapterLuxon}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        marginTop: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <DatePicker
                        label={'Startdatum'}
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        disableMaskedInput={true}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size={'small'}
                                sx={{ minWidth: 200 }}
                            />
                        )}
                    />
                    <DatePicker
                        label={'Enddatum'}
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        disableMaskedInput={true}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size={'small'}
                                sx={{ minWidth: 200 }}
                            />
                        )}
                    />
                    <Button
                        variant={'contained'}
                        startIcon={isLoading ? <CircularProgress size={20} color={'inherit'} /> : <FilterListIcon />}
                        onClick={handleFilter}
                        disabled={isFilterDisabled}
                        sx={{ height: 40 }}
                    >
                        Filter
                    </Button>
                    {hasFilteredData && (
                        <>
                            <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                                Gefiltert: {startDate?.toFormat('dd.MM.yyyy')} - {endDate?.toFormat('dd.MM.yyyy')}
                            </Typography>
                            <Button
                                variant={'contained'}
                                startIcon={<ClearIcon />}
                                onClick={handleReset}
                                sx={{ height: 40 }}
                            >
                                Zurücksetzen
                            </Button>
                        </>
                    )}
                </Box>
            </LocalizationProvider>

            {/* Overall Scores */}
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 2, gap: 3, flexWrap: 'wrap' }}>
                <Card sx={cardStyle}>
                    <Typography variant={'h4'}>Gesamtstimmen</Typography>
                    <Typography variant={'h2'} sx={{ margin: 'auto' }}>{overallVotes}</Typography>
                </Card>

                <Card sx={cardStyle}>
                    <Typography variant={'h4'}>Durchschnitt</Typography>
                    <Typography variant={'h2'} sx={{ margin: 'auto' }}>{overallScore}★</Typography>
                </Card>

                <Card sx={cardStyle}>
                    <Typography variant={'h4'}>Höchste Bewertung</Typography>
                    <Typography variant={'h2'} sx={{ margin: 'auto' }}>{highestScore}★</Typography>
                </Card>

                <Card sx={cardStyle}>
                    <Typography variant={'h4'}>Niedrigste  Bewertung</Typography>
                    <Typography variant={'h2'} sx={{ margin: 'auto' }}>{lowestScore}★</Typography>
                </Card>

            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                {isAdmin && React.createElement(
                    CSVLink as any,
                    {...csvReport, onClick: () => {}},
                    <Button
                        children={'Übersicht exportieren'}
                        variant={'contained'}
                    />,
                )}
            </Box>

            {/* Company Score List */}
            <Card sx={{ marginTop: 2, flex: 1, width: '100%' }}>
                <DataGrid
                    columns={columns}
                    rows={rows}
                    disableSelectionOnClick={true}
                    disableColumnFilter={true}
                    disableColumnSelector={false}
                    disableDensitySelector={true}
                    disableColumnMenu={true}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 200 },
                            csvOptions: {
                                delimiter: ';',
                                fileName: 'Brandenburger Lieblingslokal',
                                allColumns: true,
                                getRowsToExport: getUnfilteredRows,
                                disableToolbarButton: !isAdmin,
                            },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    sortModel={sortModel}
                    onSortModelChange={(model => setSortModel(model))}
                />
            </Card>
        </Box>
    );
};

export default AdminPage;
